import { useTranslation } from 'react-i18next';
import type { DocumentStatus, ContainerStatus, RiskLevel, DocumentType } from '@/types/container';

export const useI18n = () => {
    const { t, i18n } = useTranslation();

    // Helper functions for translating types/enums
    const translateDocumentStatus = (status: DocumentStatus): string => {
        const statusMap: Record<DocumentStatus, string> = {
            Completo: 'documentStatus.complete',
            Pending: 'documentStatus.pending',
            Error: 'documentStatus.error',
        };
        return t(statusMap[status] || status, { ns: 'types' });
    };

    const translateContainerStatus = (status: ContainerStatus): string => {
        const statusMap: Record<ContainerStatus, string> = {
            'In Transit': 'containerStatus.inTransit',
            'Delivered': 'containerStatus.delivered',
            'Delayed': 'containerStatus.delayed',
            'At Port': 'containerStatus.atPort',
        };
        return t(statusMap[status] || status, { ns: 'types' });
    };

    const translateRiskLevel = (level: RiskLevel): string => {
        const levelMap: Record<RiskLevel, string> = {
            Low: 'riskLevel.low',
            Medium: 'riskLevel.medium',
            High: 'riskLevel.high',
            Critical: 'riskLevel.critical',
        };
        return t(levelMap[level] || level, { ns: 'types' });
    };

    const translateDocumentType = (type: DocumentType): string => {
        const typeMap: Record<DocumentType, string> = {
            bill_of_lading: 'documentType.billOfLading',
            invoice: 'documentType.invoice',
            phytosanitary_certificate: 'documentType.phytosanitaryCertificate',
            movement_certificate: 'documentType.movementCertificate',
            packing_list: 'documentType.packingList',
            fumigation_certificate: 'documentType.fumigationCertificate',
            sad_icm: 'documentType.sadIcm',
            coo: 'documentType.coo',
            unknown: 'documentType.unknown',
        };
        return t(typeMap[type] || type, { ns: 'types' });
    };

    const translateRole = (role: string): string => {
        const roleMap: Record<string, string> = {
            'Autorità': 'roles.authority',
            'Freight Forwarder': 'roles.freightForwarder',
            'Sales': 'roles.sales',
            'Spedizioniere': 'roles.spedizioniere',
            'Dogana': 'roles.dogana',
            'Vettore marittimo': 'roles.vettoreMarittimo',
            'Trasporto terrestre': 'roles.trasportoTerrestre',
            'Porto imbarco': 'roles.portoImbarco',
            'Porto di sbarco': 'roles.portoSbarco',
            'Staff aeroporto': 'roles.staffAeroporto',
            'Armatore': 'roles.armatore',
        };
        return roleMap[role] ? t(roleMap[role], { ns: 'types' }) : role;
    };

    const changeLanguage = (lang: 'en' | 'it' | 'es') => {
        i18n.changeLanguage(lang);
        localStorage.setItem('i18nextLng', lang);
    };

    const currentLanguage = i18n.language || 'en';

    return {
        t,
        i18n,
        changeLanguage,
        currentLanguage,
        translateDocumentStatus,
        translateContainerStatus,
        translateRiskLevel,
        translateDocumentType,
        translateRole,
    };
};

